
// components/AppFooter.js
import React from "react";

/**
 * AppFooter
 *
 * - Renders up to 5 buttons in a fixed footer bar.
 * - Buttons are evenly spaced across the width (LinkedIn-style).
 * - Each button shows an icon and a short label.
 * - Handles non-array inputs and overflows in dev via console warnings.
 *
 * Expected shape for each button:
 * {
 *   id?: string;          // optional stable key
 *   icon: ReactNode;      // icon component (e.g. <FaHome />)
 *   label?: string;       // short text like "Home"
 *   onClick?: (evt) => void;
 *   disabled?: boolean;
 *   testId?: string;      // optional testing hook
 * }
 */
function AppFooter({ buttons }) {
  // Always normalize to an array; log a warning in dev if not.
  const rawButtons = Array.isArray(buttons) ? buttons : [];

  if (!Array.isArray(buttons) && process.env.NODE_ENV !== "production") {
    // Non-fatal, but lets you know something is off in dev.
    console.warn(
      "AppFooter: `buttons` prop should be an array. Received:",
      typeof buttons
    );
  }

  // Enforce a hard max of 5 buttons, ignoring extras.
  const safeButtons = rawButtons.slice(0, 5);

  if (rawButtons.length > 5 && process.env.NODE_ENV !== "production") {
    console.warn(
      `AppFooter: received ${rawButtons.length} buttons. ` +
        "Only the first 5 will be rendered to maintain layout."
    );
  }

  // If there are no buttons to render, don't show an empty footer bar.
  if (safeButtons.length === 0) {
    return null;
  }

  // Expose the count as a CSS variable so spacing can adapt in CSS.
  const footerStyle = {
    "--footer-button-count": safeButtons.length || 1,
  };

  return (
    <div className="footer-container" style={footerStyle}>
      {safeButtons.map((button, index) => {
        // Defensive destructuring in case a button entry is null/undefined.
        const { label, icon, onClick, disabled, id, testId } = button || {};
        const key = id ?? index;

        const handleClick = (event) => {
          // Only call onClick if it's a function and the button isn't disabled.
          if (typeof onClick === "function" && !disabled) {
            onClick(event);
          }
        };

        return (
          <button
            key={key}
            type="button"
            className="footer-button"
            onClick={handleClick}
            disabled={!!disabled}
            data-testid={testId}
          >
            {/* Icon is expected to be a small React component (Fa*, etc.) */}
            {icon}
            {/* Keep labels optional and short (e.g., "Home", "Events"). */}
            {label && <div className="footer-label">{label}</div>}
          </button>
        );
      })}
    </div>
  );
}

export default AppFooter;
